import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MessageCircle, Send, Sparkles, Users, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import PushBroadcast from "@/components/sections/PushBroadcast";
import PushToggle from "@/components/PushToggle";

const TEMPLATES = [
  { label: "Prossima riunione", text: "Ciao a tutti! Vi ricordo la nostra prossima riunione. Dettagli sulla piattaforma. 📅\nhttps://app.amunicassibile.it" },
  { label: "Nuova votazione", text: "È aperta una nuova votazione sulla piattaforma dell'Associazione. Il vostro voto conta! 🗳️\nhttps://app.amunicassibile.it" },
  { label: "Nuovo evento", text: "Nuovo evento in arrivo! 🎉 Scoprite tutti i dettagli sulla piattaforma dell'Associazione.\nhttps://app.amunicassibile.it" },
  { label: "Nuovo bilancio", text: "Abbiamo pubblicato il nuovo bilancio nella sezione Trasparenza. Amunì trasparente per tutti! 📊\nhttps://app.amunicassibile.it/trasparenza" },
  { label: "Reminder tessera", text: "Ricordiamo di rinnovare la tessera per l'anno in corso. Trovate tutto nella sezione Tessera dell'app. 💳" },
];

export default function WhatsAppSection({ isAdmin }) {
  const [status, setStatus] = useState(null);
  const [text, setText] = useState("");

  useEffect(() => { api.get("/whatsapp/status").then(r => setStatus(r.data)); }, []);

  const sendToWhatsApp = () => {
    if (!text.trim()) { toast.error("Scrivi prima il messaggio"); return; }
    // Log the broadcast attempt (best-effort, non-blocking)
    if (isAdmin) api.post("/whatsapp/broadcast", { body: text }).catch(() => {});
    // wa.me deep link: opens WhatsApp with pre-composed text → admin picks the community group
    const link = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(link, "_blank");
    toast.success("WhatsApp aperto — scegli il gruppo Community e tocca invia");
  };

  const openCommunity = () => {
    if (!status?.community_invite_url) {
      toast.error("Prima imposta il link del gruppo in Impostazioni");
      return;
    }
    window.open(status.community_invite_url, "_blank");
  };

  return (
    <div data-testid="section-whatsapp">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#16A34A] font-bold flex items-center gap-2"><MessageCircle className="w-3 h-3" /> Community</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Messaggi Community</h1>
        <p className="text-[#6B6558] mt-2">Un click e WhatsApp si apre col messaggio già scritto. Tocchi il gruppo Community e invii. Semplice.</p>
      </div>

      <div className="mb-6 max-w-sm"><PushToggle /></div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 paper rounded-2xl p-6">
          <div className="font-display text-xl font-bold text-[#2B3A33] mb-4">Componi messaggio</div>

          <div className="mb-4">
            <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Template rapidi</div>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.label} data-testid={`wa-template-${t.label}`} onClick={() => setText(t.text)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#E8E2D8] hover:bg-[#F3EEE4]">
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <textarea
            data-testid="wa-textarea"
            placeholder="Scrivi il tuo messaggio per la community…"
            rows={7}
            value={text}
            onChange={e => setText(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-[#E8E2D8] bg-white resize-none"
          />
          <div className="text-xs text-[#8A8272] mt-2">{text.length} caratteri</div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              data-testid="wa-send-btn"
              onClick={sendToWhatsApp}
              disabled={!text.trim()}
              className="wa-btn px-5 py-3 rounded-xl font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> Apri WhatsApp con messaggio pronto
            </button>
            {status?.community_invite_url && (
              <button
                data-testid="wa-open-community-btn"
                onClick={openCommunity}
                className="px-5 py-3 rounded-xl font-semibold border border-[#E8E2D8] bg-white hover:bg-[#F3EEE4] inline-flex items-center gap-2"
              >
                <Users className="w-4 h-4" /> Vai al Gruppo
              </button>
            )}
          </div>
        </div>

        <div className="paper rounded-2xl p-6" style={{background:"linear-gradient(180deg, #E8FEE9, #FFFFFF)"}}>
          <div className="w-12 h-12 rounded-xl bg-[#16A34A] text-white flex items-center justify-center mb-4">
            <MessageCircle className="w-6 h-6" />
          </div>
          <div className="font-display text-xl font-bold text-[#2B3A33]">Come funziona</div>
          <ol className="text-sm text-[#3C4A44] mt-3 space-y-2 list-decimal list-inside">
            <li>Scegli un template o scrivi il messaggio</li>
            <li>Tocca <strong>"Apri WhatsApp con messaggio pronto"</strong></li>
            <li>WhatsApp si apre con il testo già scritto</li>
            <li>Tocca il gruppo Community → <strong>Invia</strong></li>
          </ol>
          <div className="mt-4 text-xs text-[#6B6558] bg-white/70 rounded-lg p-3 border border-[#E8E2D8]">
            <div className="font-semibold text-[#2B3A33] mb-1 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Perché così?</div>
            L'API WhatsApp <strong>non permette</strong> di postare direttamente nei gruppi (limitazione di Meta, vale per tutti: Twilio, Cloud API, ecc.). Con il deep-link risolvi in un click, gratis e senza opt-in.
          </div>
          {!status?.community_invite_url && (
            <div className="mt-3 text-xs text-[#92400E] bg-[#FEF3C7] rounded-lg p-3 border border-[#FCD34D]">
              💡 <strong>Suggerimento:</strong> imposta il link del gruppo Community in <em>Impostazioni</em> per avere il pulsante "Vai al Gruppo".
            </div>
          )}
        </div>
      </div>
      {isAdmin && <div className="mt-6"><PushBroadcast /></div>}
    </div>
  );
}
